import type { ThreadReportRequest } from '@peles/shared';
import {
  attachLibraryFiles,
  attachThreadReports,
  requestThreadUpload,
  type ThreadUploadLinkInput,
} from '../../api/threadTools';

/**
 * What somebody asked a conversation to carry, separated from *when* it happens
 * (COMMUNICATIONS-DESIGN H4).
 *
 * ⚠️ **The tools outlived the assumption that a thread already exists.** They
 * were written against a `threadId` and lived only beside an open conversation,
 * so starting one offered none of them — a producer who wanted to send the
 * summary with the opening message had to start the conversation, find it, and
 * attach the report as a second act. Nothing about choosing a file or a report
 * needs the thread; only *applying* the choice does.
 *
 * So a choice is an intent, and the intent is applied to a thread id whenever
 * one is available: at once on an open conversation, and after `POST /threads`
 * on a new one. One toolbar, one set of dialogs, and no second implementation
 * of "attach a report" that will drift from the first.
 */
export interface ConversationToolIntent {
  /** Files the library already holds, by id — never by object key (rule C5a). */
  fileIds?: string[];
  reports?: ThreadReportRequest[];
  /**
   * A request for a file back.
   *
   * At most one, deliberately: everybody on the conversation gets their own
   * single-use link from it, and a second request is a second round of emails
   * asking for the same thing.
   */
  upload?: ThreadUploadLinkInput;
}

/**
 * One more choice folded into what is already staged.
 *
 * ⚠️ **Files and reports accumulate; a file request replaces.** Picking twice
 * means both sets of files — that is what a second visit to the picker means —
 * whereas reopening the request dialog is editing the one request, not adding
 * another. File ids are de-duplicated because the picker cannot know what an
 * earlier visit chose, and attaching the same file twice would put two
 * identical rows on the transcript.
 */
export function mergeToolIntent(
  current: ConversationToolIntent,
  incoming: ConversationToolIntent,
): ConversationToolIntent {
  const merged: ConversationToolIntent = { ...current };

  if (incoming.fileIds !== undefined) {
    merged.fileIds = [...new Set([...(current.fileIds ?? []), ...incoming.fileIds])];
  }
  if (incoming.reports !== undefined) {
    merged.reports = [...(current.reports ?? []), ...incoming.reports];
  }
  if (incoming.upload !== undefined) {
    merged.upload = incoming.upload;
  }
  return merged;
}

/**
 * Whether a file request waits for the next message or goes as its own.
 *
 * ⚠️ **The note is the ask, and whether one was written decides this.** Both
 * behaviours are right for different moments and the dialog cannot tell them
 * apart on its own. Somebody who typed "please send the signed contract" has
 * *written* their request and pressed a button labelled send: holding it back
 * until they happen to post another message loses the sentence they typed and
 * makes the button a lie. Somebody who typed nothing is about to say it in the
 * conversation, and sending now would email an ask with no words in it — the
 * two-emails-for-one-act fault this staging was built to fix.
 *
 * ⚠️ **Exported so the wording and the behaviour cannot disagree.** The button
 * reads "Send the link" or "Add to next message" off this same call, which is
 * what stopped it promising one and doing the other.
 *
 * `deferrable` is false where there is nothing to defer *to* — a conversation
 * being started has no next message, its first message is the ask.
 */
export function uploadRidesNextMessage(
  upload: ThreadUploadLinkInput,
  deferrable: boolean,
): boolean {
  return deferrable && (upload.note ?? '').trim() === '';
}

/** Whether anything at all is staged — an empty intent is not worth applying. */
export function hasToolIntent(intent: ConversationToolIntent): boolean {
  return (
    (intent.fileIds?.length ?? 0) > 0 ||
    (intent.reports?.length ?? 0) > 0 ||
    intent.upload !== undefined
  );
}

function count(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

/**
 * What is staged, in the words the sender would use.
 *
 * Shown while the conversation is still being written, because a choice made in
 * a dialog that has since closed is otherwise invisible until it has already
 * happened.
 */
export function summariseToolIntent(intent: ConversationToolIntent): string[] {
  const parts: string[] = [];
  if (intent.fileIds?.length) parts.push(count(intent.fileIds.length, 'file'));
  if (intent.reports?.length) parts.push(count(intent.reports.length, 'report'));
  if (intent.upload) parts.push('a request for a file back');
  return parts;
}

/**
 * Carry out what was chosen, against a conversation that now exists.
 *
 * Answers a line per thing done, for the same reason the upload request answers
 * two numbers: "3 files, 0 reports" is the honest report of a half-failure, and
 * a single "Done" would hide it.
 *
 * ⚠️ **Sequential, not `Promise.all`.** Each of these writes to the same thread
 * and posts to the same transcript; running them together interleaves the
 * messages, and a failure part-way through would leave the caller unable to say
 * which of them landed.
 *
 * ⚠️ **`onRemaining` is what makes a retry safe.** None of these is idempotent:
 * attaching the same files again puts a second set of rows on the transcript,
 * and asking again emails everybody a *second* single-use link. So each step
 * reports what is left the moment it succeeds, and a caller that retries from
 * that does only the part that failed. Without it, one failed report would cost
 * a duplicate of everything that had already worked.
 */
export async function applyConversationTools(
  threadId: string,
  intent: ConversationToolIntent,
  onRemaining?: (remaining: ConversationToolIntent) => void,
): Promise<string[]> {
  const outcomes: string[] = [];
  const remaining: ConversationToolIntent = { ...intent };

  const done = (key: keyof ConversationToolIntent) => {
    delete remaining[key];
    onRemaining?.({ ...remaining });
  };

  if (intent.fileIds?.length) {
    const attached = await attachLibraryFiles(threadId, intent.fileIds);
    done('fileIds');
    outcomes.push(`Attached ${count(attached.length, 'file')}.`);
  }

  if (intent.reports?.length) {
    const attached = await attachThreadReports(threadId, intent.reports);
    done('reports');
    outcomes.push(`Attached ${count(attached.length, 'report')}.`);
  }

  if (intent.upload) {
    const result = await requestThreadUpload(threadId, intent.upload);
    done('upload');
    // Both numbers, because they differ when the mail provider is having a bad
    // day and "5 links, 0 emailed" is the only honest way to say so.
    outcomes.push(
      `Requested a file from ${result.requested} ${
        result.requested === 1 ? 'person' : 'people'
      } — ${result.emailed} emailed.`,
    );
  }

  return outcomes;
}
